// Slide-doc JSON schema v0.2 — additive over v1, all new fields optional.
// Logical stage is 1920x1080 (landscape) or 1080x1920 (portrait); Stage scales to viewport.

export type Color = string;
export type TierCode = 'free' | 'premium' | 'ultra_premium' | 'legend';

export interface DeckDoc {
  id: string;
  title: string;
  slides: SlideDoc[];

  // v0.2 — all optional
  schemaVersion?: 1 | 2;
  chapters?: Chapter[];
  narration?: NarrationTrack;
  tier_required?: TierCode;
  series_samid?: string;
  set_samid?: string;
}

export interface NarrationTrack {
  src: string;             // R2 URL OR public-relative path
  durationMs: number;
  waveformPeaks?: number[]; // pre-computed for scrub UI (0..1 per bucket)
  sampleRate?: number;     // recording metadata
}

export interface Chapter {
  id: string;
  title: { en: string; bn?: string } | string;
  slideId: string;         // entry slide for the chapter
  thumbnailShapeId?: string;
  // atMs computed by playhead from cumulative slide durations
}

export interface SlideDoc {
  id: string;
  background?: Color;
  shapes: Shape[];
  animations?: Animation[];
  audio?: AudioCue[];
  durationMs?: number;     // explicit slide duration; if absent computed from animations

  // v0.2
  beats?: Beat[];
  quizzes?: SlideQuizRef[];
  branches?: BranchPoint[];
  interactiveShapes?: InteractiveShape[];
  chapterId?: string;      // back-reference for tooling
}

export type Shape =
  | ImageShape
  | CircleShape
  | RectShape
  | TextShape;

interface BaseShape {
  id: string;
  anchor?: 'tl' | 'center';
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  opacity?: number;
  zIndex?: number;
}

export interface ImageShape extends BaseShape {
  type: 'image';
  src: string;
  alt?: string;
}

export interface CircleShape extends BaseShape {
  type: 'circle';
  stroke?: Color;
  strokeWidth?: number;
  fill?: Color;
}

export interface RectShape extends BaseShape {
  type: 'rect';
  fill?: Color;
  stroke?: Color;
  strokeWidth?: number;
  radius?: number;
}

export interface TextShape extends BaseShape {
  type: 'text';
  text: string;
  fontSize?: number;
  fontWeight?: number | string;
  color?: Color;
  align?: 'left' | 'center' | 'right';
  fontFamily?: string;
}

export type Effect =
  | 'fadeIn'
  | 'fadeOut'
  | 'scalePop'
  | 'scaleTo'
  | 'drawStroke'
  | 'rotate'
  | 'moveTo'
  | 'slideIn';

export interface Animation {
  target: string;
  effect: Effect;
  atMs: number;
  durationMs: number;
  ease?: string;
  fromScale?: number;
  toScale?: number;
  peakScale?: number;
  toX?: number;
  toY?: number;
  toRotation?: number;
  fromOffsetX?: number;
  fromOffsetY?: number;
}

export interface AudioCue {
  id: string;
  src: string;             // 'synth:<name>' or url
  atMs: number;
  volume?: number;
  loop?: boolean;
}

// ─── Interactivity types (v0.2) ─────────────────────────────────────────

// (a) Click-to-continue beats
export interface Beat {
  id: string;
  atMs: number;             // relative to slide enter
  resumesOn?: 'tap' | 'auto+Nms';
  autoResumeMs?: number;
  pulseShapeId?: string;    // shape to pulse as "tap to continue" cue
  label?: { en: string; bn?: string } | string;
}

// (b) Pause-and-quiz overlay — aligns with nus_inventory.interactive_questions JSONB
export interface SlideQuizRef {
  id: string;
  atMs: number;
  mcqSamid: string;
  blocking?: boolean;       // must answer to proceed (default true)
  scoring?: boolean;        // attempt logged (default true)
  modeOverride?: 'inline' | 'fullscreen';
}

// (c) Clickable shapes during playback
export interface InteractiveShape {
  shapeId: string;
  activeFromMs: number;
  activeUntilMs: number;
  onTap: TapAction;
}

export type TapAction =
  | { kind: 'reveal'; targetShapeId: string; effect?: 'fadeIn' | 'scalePop' | 'expand' }
  | { kind: 'jumpTo'; targetSlideId: string }
  | { kind: 'openOverlay'; html: string }
  | { kind: 'pauseAndAnnotate'; text: string };

// (d) Branching path
export interface BranchPoint {
  id: string;
  atMs: number;
  prompt: { en: string; bn?: string } | string;
  options: BranchOption[];
}

export interface BranchOption {
  id: string;
  label: { en: string; bn?: string } | string;
  jumpTo: { slideId: string } | { chapterId: string };
  scoreDelta?: number;
}

// Helper for i18n labels
export function localizedText(t: { en: string; bn?: string } | string | undefined): string {
  if (!t) return '';
  if (typeof t === 'string') return t;
  return t.en ?? t.bn ?? '';
}
